import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ShieldCheck, Activity, Clock } from "lucide-react";
import FlowVisualization from "@/components/FlowVisualization";
import { formatBytes, formatDuration, apiBaseUrl } from "@/lib/api";

const BACKEND = apiBaseUrl();

/**
 * Public, unauthenticated share page. Polls every 2s for live updates.
 * Heavily redacted payload (no tenant, no agent ids, no paths) — served
 * directly by /api/public/share/{token}.
 */
export default function SharePublic() {
    const { token } = useParams();
    const [payload, setPayload] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        const fetchOnce = async () => {
            try {
                const { data } = await axios.get(`${BACKEND}/api/public/share/${token}`, {
                    // Public endpoint — no cookies / headers.
                    withCredentials: false,
                });
                if (!mounted) return;
                setPayload(data);
                setErr("");
            } catch (e) {
                if (!mounted) return;
                const status = e?.response?.status;
                if (status === 410) setErr("This link has expired or been revoked.");
                else if (status === 404) setErr("This link is invalid.");
                else if (status === 429) { /* rate-limited, keep trying */ }
                else setErr(e?.response?.data?.detail || "Unable to load migration.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchOnce();
        const iv = setInterval(fetchOnce, 2000);
        return () => {
            mounted = false;
            clearInterval(iv);
        };
    }, [token]);

    if (loading) {
        return (
            <Shell>
                <div className="py-20 text-center font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Loading migration…
                </div>
            </Shell>
        );
    }

    if (err) {
        return (
            <Shell>
                <div className="mx-auto max-w-md py-20 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]">
                        <AlertTriangle size={26} />
                    </div>
                    <div className="mt-4 font-display text-xl font-bold">Link unavailable</div>
                    <p className="mt-2 text-sm text-muted-foreground" data-testid="share-error">
                        {err}
                    </p>
                </div>
            </Shell>
        );
    }

    const j = payload.job;
    const progressPct = Math.min(100, (j.progress || 0) * 100);
    const done = ["succeeded", "completed_with_errors", "failed", "canceled"].includes(j.status);
    const running = j.status === "running";

    return (
        <Shell>
            {/* Status hero */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        <Activity size={12} className="text-[hsl(var(--accent))]" />
                        Live migration · read-only view
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight" data-testid="share-job-name">
                        {j.name || "Migration"}
                    </h1>
                </div>
                <StatusBadge status={j.status} />
            </div>

            {/* Flow visualization — the user's key ask */}
            <FlowVisualization
                source={{ name: payload.source_name }}
                destination={{ name: payload.destination_name }}
                status={j.status}
                speedMbps={j.current_speed_mbps || j.avg_speed_mbps || 0}
                progressPct={progressPct}
                mode={j.is_rollback ? "rollback" : j.mode}
            />

            {/* Progress bar */}
            <div className="mt-5 rounded-xl border border-border bg-surface p-5">
                <div className="flex items-end justify-between">
                    <div>
                        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                            Progress
                        </div>
                        <div
                            className="mt-1 font-display text-4xl font-bold tabular-nums"
                            data-testid="share-progress-pct"
                        >
                            {progressPct.toFixed(1)}%
                        </div>
                    </div>
                    <div className="text-right font-mono text-xs text-muted-foreground">
                        <div data-testid="share-bytes">
                            {formatBytes(j.bytes_done)} / {formatBytes(j.total_bytes)}
                        </div>
                        <div data-testid="share-files">
                            {j.files_done} / {j.total_files} files
                        </div>
                    </div>
                </div>
                <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] transition-all"
                        style={{ width: `${progressPct}%` }}
                        data-testid="share-progress-bar"
                    />
                </div>
            </div>

            {/* Stats row */}
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat
                    icon={Activity}
                    label="Current speed"
                    value={running ? `${(j.current_speed_mbps || 0).toFixed(1)} MB/s` : "—"}
                    color="#3b82f6"
                    testId="share-stat-speed"
                />
                <Stat
                    icon={Clock}
                    label={done ? "Duration" : "ETA"}
                    value={
                        done
                            ? formatDuration((j.completed_at && j.started_at) ? (new Date(j.completed_at) - new Date(j.started_at)) / 1000 : null)
                            : j.eta_seconds
                              ? formatDuration(j.eta_seconds)
                              : "—"
                    }
                    color="#7c3aed"
                    testId="share-stat-eta"
                />
                <Stat
                    icon={ShieldCheck}
                    label="Integrity"
                    value={`${payload.integrity_rate.toFixed(1)}%`}
                    color={payload.integrity_rate === 100 ? "#16a34a" : "#ea580c"}
                    testId="share-stat-integrity"
                />
                <Stat
                    icon={CheckCircle2}
                    label="Errors"
                    value={j.error_count || 0}
                    color={(j.error_count || 0) > 0 ? "#dc2626" : "#16a34a"}
                    testId="share-stat-errors"
                />
            </div>

            {/* Trust line */}
            <div className="mt-4 rounded-xl border border-border bg-surface p-4 text-xs text-muted-foreground">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <TrustPill label="Encryption" value={payload.encryption} />
                    <TrustPill label="Hashing" value={payload.hash} />
                    <TrustPill label="Transport" value="Direct peer-to-peer" />
                </div>
                <div className="mt-2 text-[11px]">
                    Files travel directly agent-to-agent and never through the MoovBytes control plane.
                </div>
            </div>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-surface">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
                    <img
                        src="/brand/moovbytes-logo.png"
                        alt="MoovBytes"
                        className="h-9 w-auto"
                    />
                    <a
                        href="https://moovbytes.io"
                        rel="noreferrer"
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground"
                    >
                        Learn more →
                    </a>
                </div>
            </header>
            <main className="mx-auto max-w-4xl px-6 py-8">{children}</main>
            <footer className="border-t border-border bg-surface">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4 text-[11px] text-muted-foreground">
                    <div>Powered by MoovBytes · Secure agent-based data migration</div>
                    <div className="font-mono uppercase tracking-[0.18em]">Read-only view</div>
                </div>
            </footer>
        </div>
    );
}

function Stat({ icon: Icon, label, value, color, testId }) {
    return (
        <div className="rounded-xl border border-border bg-surface p-4" data-testid={testId}>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <Icon size={12} style={{ color }} /> {label}
            </div>
            <div className="mt-2 font-display text-xl font-bold tabular-nums">{value}</div>
        </div>
    );
}

function TrustPill({ label, value }) {
    return (
        <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.14em]">
            <span className="text-muted-foreground/70">{label}:</span>
            <span className="font-semibold text-foreground">{value}</span>
        </span>
    );
}

function StatusBadge({ status }) {
    const map = {
        succeeded: { bg: "#dcfce7", fg: "#16a34a", label: "Completed" },
        running: { bg: "#dbeafe", fg: "#2563eb", label: "In Progress" },
        queued: { bg: "#fef3c7", fg: "#b45309", label: "Queued" },
        paused: { bg: "#f1f5f9", fg: "#64748b", label: "Paused" },
        failed: { bg: "#fee2e2", fg: "#dc2626", label: "Failed" },
        completed_with_errors: { bg: "#ffedd5", fg: "#ea580c", label: "Completed · errors" },
        canceled: { bg: "#f1f5f9", fg: "#64748b", label: "Canceled" },
    };
    const m = map[status] || map.queued;
    return (
        <span
            className="rounded-full px-3 py-1 text-xs font-semibold"
            style={{ background: m.bg, color: m.fg }}
            data-testid="share-status-badge"
        >
            {m.label}
        </span>
    );
}
